import { useEffect, useState } from "react";
import axios from "axios";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminRentPayments() {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [updatingId, setUpdatingId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editStatus, setEditStatus] = useState("");
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        loadSubmissions();
    }, []);

    const loadSubmissions = async () => {
        setLoading(true);
        try {
            const token = await auth.currentUser.getIdToken();
            const res = await axios.get("/api/rent", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setSubmissions(res.data.submissions || []);
        } catch (err) {
            console.error("Failed to load rent submissions:", err);
            toast.error("Failed to load rent submissions");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (submissionId, currentStatus) => {
        setEditingId(submissionId);
        setEditStatus(currentStatus);
    };

    const saveStatusChange = async (submissionId) => {
        if (!editStatus) return;

        setUpdatingId(submissionId);
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.patch(
                `/api/rent/${submissionId}`,
                { status: editStatus },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            toast.success("Status updated");
            loadSubmissions();
            setEditingId(null);
        } catch (err) {
            console.error("Failed to update status:", err);
            toast.error("Failed to update status");
        } finally {
            setUpdatingId(null);
        }
    };

    const deleteSubmission = async (submissionId, fullName) => {
        const confirmed = await confirmSwal({
            title: "Delete Submission?",
            text: `Delete rent payment submission from ${fullName}?`,
            confirmButtonText: "Delete",
        });

        if (!confirmed) return;

        try {
            const token = await auth.currentUser.getIdToken();
            await axios.delete(`/api/rent/${submissionId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Submission deleted");
            loadSubmissions();
        } catch (err) {
            console.error("Failed to delete submission:", err);
            toast.error("Failed to delete submission");
        }
    };

    const exportToCSV = async () => {
        setExporting(true);
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await axios.get("/api/rent/export/csv", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            // Extract filename from Content-Disposition header or use default
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'rent_submissions.csv';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename=(?:"([^"]*)"|([^;\s]*))/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success("Export successful");
        } catch (err) {
            console.error("Failed to export:", err);
            toast.error("Failed to export data");
        } finally {
            setExporting(false);
        }
    };

    // Filter submissions by search query
    const filteredSubmissions = submissions.filter((submission) =>
        submission.full_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="container mt-2">
            <h3 className="mb-3">Rent Payment Submissions</h3>

            <div className="alert alert-info mb-3" role="alert">
                <strong>Note:</strong> Payment status cannot be automatically inferred from PayPal. You must manually check and update each submission's status as necessary.
            </div>

            <div className="row mb-3">
                <div className="col-md-6">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="col-md-6 text-end">
                    <button
                        className="btn btn-admin-outline btn-sm me-2"
                        onClick={loadSubmissions}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Refresh"}
                    </button>
                    {submissions.length > 0 && (
                        <button
                            className="btn btn-admin btn-sm"
                            onClick={exportToCSV}
                            disabled={exporting}
                        >
                            {exporting ? "Exporting..." : "Export to CSV"}
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <p className="text-muted">Loading submissions...</p>
            ) : filteredSubmissions.length === 0 ? (
                <p className="text-muted">
                    {searchQuery ? "No submissions matching your search." : "No rent payment submissions yet."}
                </p>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Address</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Submitted</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSubmissions
                                .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))
                                .map((submission) => (
                                    <tr key={submission._id}>
                                        <td>
                                            <strong>{submission.full_name}</strong>
                                        </td>
                                        <td>{submission.address}</td>
                                        <td>${submission.payment_amount.toFixed(2)}</td>
                                        <td>
                                            {editingId === submission._id ? (
                                                <div className="d-flex gap-2">
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={editStatus}
                                                        onChange={(e) => setEditStatus(e.target.value)}
                                                        style={{ width: "120px" }}
                                                    >
                                                        <option value="Pending">Pending</option>
                                                        <option value="Complete">Complete</option>
                                                    </select>
                                                    <button
                                                        className="btn btn-admin btn-sm"
                                                        onClick={() => saveStatusChange(submission._id)}
                                                        disabled={updatingId === submission._id}
                                                    >
                                                        {updatingId === submission._id ? "..." : "Save"}
                                                    </button>
                                                    <button
                                                        className="btn btn-admin-outline btn-sm"
                                                        onClick={() => setEditingId(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <div>
                                                    <span
                                                        className={`badge ${
                                                            submission.status === "Complete"
                                                                ? "bg-success"
                                                                : "bg-warning"
                                                        }`}
                                                    >
                                                        {submission.status}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <small className="text-muted">
                                                {new Date(submission.submitted_at).toLocaleString()}
                                            </small>
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-admin-outline btn-sm me-2"
                                                onClick={() => handleStatusChange(submission._id, submission.status)}
                                                disabled={editingId !== null}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn-admin-outline btn-sm"
                                                onClick={() => deleteSubmission(submission._id, submission.full_name)}
                                                disabled={editingId !== null}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Additional Notes Section */}
            {filteredSubmissions.length > 0 && (
                <div className="mt-4">
                    <h5>Recent Submission Details</h5>
                    {filteredSubmissions.slice(0, 5).map((submission) => (
                        <div key={submission._id} className="card mb-2 p-3">
                            <div className="d-flex justify-content-between">
                                <strong>{submission.full_name}</strong>
                                <small className="text-muted">
                                    {new Date(submission.submitted_at).toLocaleString()}
                                </small>
                            </div>
                            <p className="mb-1">
                                <small>
                                    <strong>Address:</strong> {submission.address}
                                </small>
                            </p>
                            <p className="mb-1">
                                <small>
                                    <strong>Amount:</strong> ${submission.payment_amount.toFixed(2)}
                                </small>
                            </p>
                            {submission.additional_notes && (
                                <p className="mb-0">
                                    <small>
                                        <strong>Notes:</strong> {submission.additional_notes}
                                    </small>
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}